import { useEffect, useState } from 'react'
import { Image as ImageIcon } from 'lucide-react'
import { cx } from '../lib/util.ts'

/**
 * A dish photo, or the neutral placeholder the layouts are designed to fall back to.
 *
 * A card is shown a provisional photo first and the source's real one seconds later,
 * so the swap has to be invisible: the incoming URL is decoded off-screen and only
 * becomes the displayed one once it is ready. Pointing an <img> straight at the new
 * src would blank the card back to the placeholder while the second image loads.
 *
 * Remote images fail in ways we cannot predict — hotlink blocks, dead CDNs, a URL that
 * turns out to be an HTML error page. Any of those keep whatever was already showing.
 */
export function DishPhoto({ src, alt, className }: { src?: string; alt: string; className?: string }) {
  const [shown, setShown] = useState<string | undefined>(undefined)

  useEffect(() => {
    if (!src || src === shown) return
    let live = true
    const probe = new Image()
    probe.referrerPolicy = 'no-referrer'
    probe.onload = () => { if (live) setShown(src) }
    probe.src = src
    // A failed probe simply never promotes the URL, so the current photo survives.
    return () => { live = false }
  }, [src, shown])

  return (
    <div className={cx('photo', className, shown && 'ready')} aria-hidden={!shown}>
      {!shown && <span className="ph"><ImageIcon size={20} strokeWidth={2.4} /></span>}
      {shown && <img src={shown} alt={alt} decoding="async" referrerPolicy="no-referrer" />}
    </div>
  )
}
